/**
 * Widget styles. These live inside the closed shadow root, so product CSS
 * cannot reach them and they cannot leak onto the product page.
 */
export const PANEL_CSS = `
:host { all: initial; }
* { box-sizing: border-box; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; }

/* Right-edge vertical tab (the Marker.io/Usersnap affordance). Deliberately
   NOT a bottom corner: bottom-right belongs to the product chatbot and
   bottom-left to the Next.js devtools badge in dev, which sat exactly on top
   of the old FAB and hid it. When the panel is open the tab rides its left
   edge, so it stays reachable as the close handle. */
.fab {
  position: fixed; right: 0; top: 40%; z-index: 2147483001;
  writing-mode: vertical-rl; padding: 12px 6px; border: none; cursor: pointer;
  border-radius: 8px 0 0 8px; letter-spacing: 1px;
  background: #7c5cff; color: #fff; font-size: 13px; font-weight: 600;
  box-shadow: -2px 2px 10px rgba(0,0,0,.28);
  transition: right 150ms ease;
}
.fab:hover { background: #6a48f5; }
.fab[data-open="true"] { right: 380px; background: #4b3aa8; }
@media (max-width: 380px) { .fab[data-open="true"] { display: none; } }

.panel {
  position: fixed; top: 0; right: 0; bottom: 0; width: 380px; max-width: 100vw;
  z-index: 2147483000; display: none; flex-direction: column;
  background: #17161d; color: #ececf1; font-size: 13px; line-height: 1.45;
  border-left: 1px solid #2c2a36; box-shadow: -6px 0 24px rgba(0,0,0,.32);
}
.panel[data-open="true"] { display: flex; }

header { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #2c2a36; }
header .title { font-weight: 600; letter-spacing: .2px; }
header .spacer { flex: 1; }
.route { font: 11px ui-monospace, Menlo, monospace; color: #b9b4d0; background: #262233; border: 1px solid #3a3550; border-radius: 999px; padding: 1px 8px; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.tabs { display: flex; border-bottom: 1px solid #2c2a36; }
.tabs button { flex: 1; background: none; border: none; color: #9c99ad; padding: 8px; cursor: pointer; font-size: 12px; border-bottom: 2px solid transparent; }
.tabs button[data-active="true"] { color: #ececf1; border-bottom-color: #7c5cff; }

.body { flex: 1; overflow-y: auto; padding: 10px 12px 24px; }
.foot { border-top: 1px solid #2c2a36; padding: 6px 12px; display: flex; align-items: center; gap: 8px; font-size: 11px; color: #8d8a9d; }

button.btn { background: #2a2735; color: #ececf1; border: 1px solid #3a3648; border-radius: 6px; padding: 5px 10px; cursor: pointer; font-size: 12px; }
button.btn:hover { background: #343044; }
button.btn.primary { background: #7c5cff; border-color: #7c5cff; color: #fff; }
button.btn.primary:hover { background: #6a48f5; }
button.btn[disabled] { opacity: .5; cursor: not-allowed; }
button.btn.active { background: #7c5cff; border-color: #7c5cff; color: #fff; }
button.icon { background: none; border: none; color: #9c99ad; cursor: pointer; font-size: 15px; padding: 2px 4px; }
button.icon:hover { color: #ececf1; }

input, textarea, select {
  width: 100%; background: #2a2638; color: #ececf1; border: 1px solid #413b52;
  border-radius: 6px; padding: 6px 8px; font-size: 12px; font-family: inherit;
}
/* Set explicitly, because the fields above are lighter than the browser's own
   dark-field assumption: the default placeholder grey reads as muddy on them.
   Same muted token as the .note class. */
::placeholder { color: #8d8a9d; opacity: 1; }
textarea { resize: vertical; min-height: 52px; }
/* The 100% above is for text fields; a checkbox stretched to the panel width
   is a 380px-wide tick box, which is what it did before this line existed. */
input[type=checkbox] { width: auto; flex: none; margin: 0; }
/* Anything clickable says so. A select and a checkbox label are as clickable
   as a button, and the panel is injected into someone else's page where the
   host's own cursor rules do not reach into this shadow root. */
select, summary, input[type=checkbox] { cursor: pointer; }
label { display: block; margin: 8px 0 3px; color: #a9a5ba; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; }

/* Three surfaces, all one nuance above the panel ground (#17161d), which
   stays dark so the panel still separates from the host page: the card
   (#1f1d28), its header and other raised blocks (#262233), and the fields
   themselves (#2a2638). */
.group { background: #1f1d28; border: 1px solid #2c2a36; border-radius: 8px; margin-bottom: 8px; overflow: hidden; }
.group > summary { cursor: pointer; padding: 7px 10px; background: #262233; font-size: 12px; color: #cfcbe0; list-style: none; display: flex; gap: 6px; align-items: center; }
.group > summary::-webkit-details-marker { display: none; }
.group > summary .count { margin-left: auto; color: #7f7c8f; font: 11px ui-monospace, Menlo, monospace; }

.item { border-top: 1px solid #26242f; padding: 8px 10px; }
.item .head { display: flex; gap: 6px; align-items: flex-start; }
.item .name { flex: 1; cursor: pointer; }
.item .statuses { display: flex; gap: 3px; }
.item .statuses button { padding: 1px 7px; font-size: 11px; }
.item .detail { margin-top: 7px; padding-left: 2px; color: #b6b2c6; font-size: 12px; }
.item .detail h4 { margin: 8px 0 3px; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; color: #8d8a9d; }
.item .detail ol, .item .detail ul { margin: 0; padding-left: 18px; }
.item .detail h4 + textarea { margin-top: 2px; }
/* A step is read-only text; only its own ⓘ sits at the end of the row rather
   than under it. How a step went is recorded on the FINDING, not here.
   The row is a flex line, which suppresses the <ol> marker — so the number
   comes back as a counter. Steps are referred to by number ("step 2 is where
   it broke"), so losing them is not cosmetic. */
.item .detail .steps { counter-reset: qa-step; list-style: none; padding-left: 0; }
.item .detail .steps li { margin-bottom: 3px; display: flex; align-items: flex-start; gap: 6px; }
.item .detail .steps li::before { counter-increment: qa-step; content: counter(qa-step) "."; color: #8d8a9d; min-width: 13px; }
.item .detail .steps li > .step-text { flex: 1; }
.item[data-status="passed"] { border-left: 3px solid #3ea36b; }
.item[data-status="failed"] { border-left: 3px solid #d6455b; }
.item[data-status="skipped"] { border-left: 3px solid #6f6b80; }

/* "What is this asking me to do?" — the ⓘ next to the verdict trio, and next
   to each step once a scenario is expanded. Quieter than a verdict button on
   purpose: asking records nothing about the scenario. */
button.btn.chat-open { padding: 1px 6px; font-size: 11px; color: #b9b4d0; }

/* The count on the edge tab while the panel is closed. Absent at zero, and
   absent entirely against a server with no inbox endpoint — a "0" would be a
   claim we cannot make. The tab is vertical, so the badge sits inline with
   the label rather than in a corner. */
.fab-badge {
  display: inline-block; margin-top: 6px; padding: 0 5px; border-radius: 999px;
  background: #fff; color: #4b3aa8; font-size: 11px; font-weight: 700;
  writing-mode: horizontal-tb;
}

/* "New for you" — replies, fixes waiting for retest, a changed checklist.
   Above the checklist rather than in a tab of its own: it is news about work
   the tester already has, not a fourth place to look. */
.inbox { background: #262233; border: 1px solid #3a3550; border-radius: 8px; padding: 8px 10px; margin-bottom: 10px; }
.inbox-head { display: flex; gap: 6px; align-items: center; }
/* The header is a heading with counts, not a button that looks like the row
   actions under it. It is still a real button (it collapses the strip), so it
   keeps a pointer and a hover — it just stops competing for the eye. */
.inbox-toggle {
  flex: 1; display: flex; gap: 6px; align-items: center; text-align: left;
  background: none; border: none; padding: 0; cursor: pointer; color: #ececf1;
}
.inbox-toggle .caret { color: #8d8a9d; font-size: 10px; width: 9px; }
.inbox-toggle:hover .inbox-title { color: #fff; }
.inbox-title { font-weight: 600; font-size: 12px; }

/* Counts, and the kind of news a row carries — the same word in the same
   colour in both places. Amber is a fix claim wherever it appears in this
   panel (the status-fixed tag, the fix-note rule); blue is the conversation
   side (the status-filed tag). */
.pill {
  font: 10px ui-monospace, Menlo, monospace; border-radius: 999px;
  padding: 1px 7px; border: 1px solid #3a3648; color: #a9a5ba; white-space: nowrap;
}
.pill.retest { color: #ffc46b; border-color: #7a5a24; background: rgba(255,196,107,.07); }
.pill.reply { color: #8fb8ff; border-color: #2f4b7a; background: rgba(143,184,255,.07); }
/* Violet: a question waiting for the tester's view. Deliberately NOT amber —
   a proposal is not a fix to verify, and borrowing the claim's colour would
   file it in the eye's retest pile. */
.pill.discussion { color: #c9b3ff; border-color: #55408a; background: rgba(201,179,255,.07); }

/* One row per finding. The coloured edge is the same device the checklist uses
   for a verdict (.item[data-status]), so "amber = somebody claims this is
   fixed" reads the same way in both lists. */
.inbox-row {
  border-top: 1px solid #322e42; margin-top: 8px; padding: 8px 0 2px 9px;
  border-left: 3px solid transparent;
}
.inbox-row.retest { border-left-color: #7a5a24; }
.inbox-row.reply { border-left-color: #2f4b7a; }
.inbox-row.discussion { border-left-color: #55408a; }
/* Dismissed: same row, the colour taken out of it — it is not news, it is the
   way back. */
.inbox-row.dismissed { border-left-color: #3a3648; opacity: .8; }
.inbox-row.dismissed:hover { opacity: 1; }
/* A strip with nothing new, kept only as the way back to dismissed claims:
   one quiet line, no box competing with the checklist under it. */
.inbox.quiet { background: none; border-color: transparent; padding: 0 10px; }
.inbox-dismissed {
  background: none; border: none; padding: 0; cursor: pointer; color: #8d8a9d;
  font: 10px ui-monospace, Menlo, monospace; white-space: nowrap;
}
.inbox-dismissed:hover, .inbox-dismissed.active { color: #d8d3e8; text-decoration: underline; }
.inbox-sub { color: #8d8a9d; font-size: 11px; margin-top: 10px; }
.inbox-row .row { gap: 6px; display: flex; flex-wrap: wrap; align-items: center; }
/* Capped in the panel as well as on the wire. The server sends an excerpt, but
   the widget is routinely NEWER than the server answering it — against an older
   one the whole fix note arrives, and an uncapped row buries its own "Open the
   finding" button under twenty lines of prose. Four lines, then it fades. */
.inbox-excerpt {
  color: #d8d3e8; margin: 2px 0; white-space: pre-wrap;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 4;
  overflow: hidden;
}
/* Context, not the action: the row's button is what the tester presses, and
   this is the page the words are about — routinely another environment. Quiet,
   one line, and it truncates rather than wrapping a URL across three. */
.inbox-link {
  display: block; margin-top: 4px; color: #7f7c8f;
  font: 10px ui-monospace, Menlo, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.inbox-link:hover { color: #b9b4d0; }

/* "Answer the claim" — the job a fixed finding gives a tester, at the top of
   its card. Raised a nuance above the card it sits in and edged in the same
   amber as the claim itself, so it reads as the thing to act on rather than as
   another paragraph of the report. */
.answer {
  background: #262233; border: 1px solid #3a3550; border-left: 3px solid #7a5a24;
  border-radius: 6px; padding: 6px 9px 9px; margin-bottom: 8px;
}
/* The same block for a question, edged violet like its inbox row. */
.answer.discuss { border-left-color: #55408a; }
.answer .question { color: #d8d3e8; white-space: pre-wrap; margin-top: 4px; }
.answer > label { margin-top: 0; }
.answer textarea { margin-top: 6px; min-height: 44px; }
button.btn.danger { border-color: #7a2f3c; color: #ff8095; }
button.btn.danger:hover { background: #3a2530; }

/* The conversation under a finding card. Same message blocks as the scenario
   chat — it is the same gesture in the same panel — with the author kinds the
   thread actually carries. */
.thread { margin-top: 8px; }
.chat-msg.developer { background: #262233; border-color: #3a3648; }
.chat-msg.agent { background: #221f2e; border-color: #3a3648; }
.chat-msg.system { background: transparent; border-style: dashed; color: #8d8a9d; }

.chat-subject { color: #ececf1; }
.chat-subject.step { margin-top: 4px; color: #b6b2c6; font-size: 12px; }
.chat-log { margin: 10px 0 4px; display: flex; flex-direction: column; gap: 8px; }
.chat-msg { border-radius: 8px; padding: 6px 9px; border: 1px solid #2c2a36; }
.chat-msg.tester { background: #2e2a3d; }
.chat-msg.assistant { background: #262233; border-color: #3a3648; }
.chat-who { font: 10px ui-monospace, Menlo, monospace; text-transform: uppercase; letter-spacing: .4px; color: #8d8a9d; margin-bottom: 3px; }
.chat-text { white-space: pre-wrap; }

/* Steps on the finding form and on a filed finding. The ✓/✕ pair is the same
   visual language as the scenario's verdict trio on purpose — it is the same
   question, asked about one line instead of the whole check. An UNMARKED step
   is "not reached", so it is styled as quiet rather than as unanswered. */
.step-results { counter-reset: qa-step; list-style: none; margin: 4px 0 0; padding-left: 0; }
.step-results li { margin-bottom: 4px; display: flex; align-items: flex-start; gap: 6px; }
.step-results li::before { counter-increment: qa-step; content: counter(qa-step) "."; color: #8d8a9d; min-width: 13px; }
.step-results li > .step-text { flex: 1; }
.step-results .statuses { display: flex; gap: 3px; }
.step-results .statuses button { padding: 1px 7px; font-size: 11px; }
/* Only on a FILED finding: there, an unmarked step is the answer "never got
   here", and reading quieter than the marked ones is the point. On the form
   every step starts unmarked, and greying the lot would read as disabled. */
.step-results.done li[data-status="unreached"] { color: #7f7c8f; }
.step-results.done { margin-top: 6px; font-size: 12px; }
.step-results.done .mark { width: 12px; text-align: center; color: #8d8a9d; }
.step-results.done li[data-status="passed"] .mark { color: #6fd39a; }
.step-results.done li[data-status="failed"] .mark { color: #ff8095; }

.finding { background: #1f1d28; border: 1px solid #2c2a36; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
/* The finding currently loaded into the form at the top of the tab. Dimmed
   and inert: every control on it would act on a finding the tester is in the
   middle of rewriting, so the form's own Save changes / Cancel are the only
   ways to release it. */
.finding.editing { opacity: .45; pointer-events: none; }
.finding .row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.finding .observed { margin: 4px 0; }
.tag { font: 10px ui-monospace, Menlo, monospace; border: 1px solid #3a3648; border-radius: 999px; padding: 0 7px; color: #a9a5ba; }
/* The finding's NAME, not a state — so it borrows no status colour and reads
   brighter than the tags around it. It is the one thing on the card a tester
   quotes out loud. */
.tag.ref { color: #ffffff; border-color: #55506b; }
.tag.high { color: #ff8095; border-color: #7a2f3c; }
.tag.medium { color: #ffc46b; border-color: #7a5a24; }
.tag.status-resolved { color: #6fd39a; border-color: #2f6b4b; }
.tag.status-filed { color: #8fb8ff; border-color: #2f4b7a; }
.tag.status-fixed { color: #ffc46b; border-color: #7a5a24; }
.tag.status-needs_discussion { color: #c9b3ff; border-color: #55408a; }
/* The finding an inbox row opened, above the page's own list. Boxed and
   labelled because it is a different claim from the list under it: "the one
   you were asked about", which may have been filed on another environment. */
.pinned { border: 1px solid #3a3550; border-radius: 8px; padding: 8px 10px; margin-bottom: 10px; background: #221f2e; }
.pinned-head { display: flex; gap: 6px; align-items: center; justify-content: space-between; }
.pinned .finding { margin-top: 6px; margin-bottom: 0; }
/* "All findings": compact rows that open into the full card. */
.all-filters { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 8px; }
.all-filters .btn { padding: 3px 8px; font-size: 11px; }
.all-row { border: 1px solid #2c2a36; border-radius: 8px; padding: 6px 8px; margin-bottom: 6px; background: #1b1a23; }
.all-row.open { border-color: #55506b; }
.all-row .finding { margin: 6px 0 0; }
.all-head { display: flex; gap: 6px; align-items: center; width: 100%; background: none; border: none; color: inherit; padding: 0; cursor: pointer; text-align: left; font: inherit; }
.all-head .caret { color: #8d8a9d; font-size: 10px; width: 9px; flex: none; }
.all-head .tag { flex: none; }
.all-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.all-head:hover .all-title { color: #fff; }
select.status-select { width: auto; font-size: 12px; padding: 4px 6px; }
.all-meta { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; margin-top: 4px; }
.all-meta .ref { margin: 0; }

/* The count on the All findings tab: what is waiting on the tester, the same
   number as the edge tab and the "Needs me" chip. */
.tab-badge {
  display: inline-block; margin-left: 5px; min-width: 16px; padding: 0 5px;
  border-radius: 999px; background: #7c5cff; color: #fff;
  font: 700 10px/16px ui-sans-serif, system-ui, sans-serif; text-align: center;
}

/* "Needs me" — whose turn it is, top-down: the one number, the three jobs
   (which double as the filter), then each job with what is waiting and its
   verb. Each job carries the strip's colour for it — blue conversation,
   violet question, amber fix claim — via --k (text), --kb (edge), --kf (fill). */
.turn-answer { --k: #8fb8ff; --kb: #2f4b7a; --kf: rgba(143,184,255,.08); }
.turn-decide { --k: #c9b3ff; --kb: #55408a; --kf: rgba(201,179,255,.08); }
.turn-retest { --k: #ffc46b; --kb: #7a5a24; --kf: rgba(255,196,107,.08); }
.turn-head {
  background: #262233; border: 1px solid #3a3550; border-radius: 10px;
  padding: 10px 12px; margin-bottom: 10px; display: flex; flex-direction: column; gap: 8px;
}
.turn-big { font-size: 17px; font-weight: 650; letter-spacing: -.2px; line-height: 1.2; }
.turn-seg { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.turn-seg button {
  display: flex; flex-direction: column; gap: 1px; align-items: flex-start; text-align: left;
  border: 1px solid var(--kb); background: var(--kf); border-radius: 7px;
  padding: 6px 8px; cursor: pointer; color: #cfcbe0; font: inherit;
}
.turn-seg button:hover { border-color: var(--k); }
.turn-seg button.on { outline: 2px solid var(--k); outline-offset: -1px; }
.turn-seg button[disabled] { opacity: .45; cursor: default; }
.turn-seg .num { font-size: 17px; font-weight: 650; line-height: 1; color: var(--k); }
.turn-seg .lb { font-size: 10.5px; }
.turn-group {
  display: flex; align-items: baseline; gap: 8px; margin: 12px 0 6px; width: 100%;
  background: none; border: none; padding: 0; cursor: pointer; font: inherit; text-align: left;
}
.turn-group .caret { color: #8d8a9d; font-size: 10px; width: 9px; flex: none; }
.turn-group:hover .h { text-decoration: underline; }
.turn-group .h {
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .6px;
  color: var(--k, #8d8a9d);
}
.turn-group .n { font: 600 11px ui-monospace, Menlo, monospace; color: #8d8a9d; }
.turn-group .rule { flex: 1; height: 1px; background: #2c2a36; align-self: center; }
/* One line until pressed: the edge says which job, the row says which finding
   and how long it has waited. Unfolded (.peek), it shows the message and the
   verbs; .open is the full card. */
.turn-card { display: flex; flex-direction: column; gap: 7px; padding: 6px 8px; margin-bottom: 4px; box-shadow: inset 3px 0 0 var(--kb); }
.turn-card.peek { border-color: var(--kb); padding-bottom: 9px; }
.turn-card.open { box-shadow: none; }
.turn-when { flex: none; margin-left: auto; padding-left: 6px; color: #8d8a9d; font: 10px ui-monospace, Menlo, monospace; }
.turn-more {
  width: 100%; margin: 2px 0 4px; padding: 5px; background: none; border: none; cursor: pointer;
  color: #a9a5ba; font-size: 11px; text-align: left; padding-left: 11px;
}
.turn-more:hover { color: #ececf1; text-decoration: underline; }
.turn-more-row { display: flex; gap: 4px; justify-content: space-between; }
.turn-more-row .turn-more { width: auto; }

/* "Need answer" / "Retest" heading: the fold on the left, a small Search on
   the right that opens an inline field under it. */
.turn-group-head { display: flex; align-items: center; gap: 6px; margin: 12px 0 6px; }
.turn-group-head .turn-group { flex: 1; margin: 0; min-width: 0; }
.turn-search-toggle {
  flex: none; background: none; border: 1px solid #3a3648; border-radius: 999px;
  color: #a9a5ba; font-size: 10.5px; padding: 1px 8px; cursor: pointer;
}
.turn-search-toggle:hover, .turn-search-toggle.on { color: #ececf1; border-color: var(--kb); }
input.turn-search { margin: 0 0 6px; padding: 4px 8px; font-size: 12px; }

/* "See all" — over the page, wider than the panel, a hundred rows a page. */
.popup-overlay {
  position: fixed; inset: 0; z-index: 2147483002; background: rgba(10,9,14,.55);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.popup {
  width: min(640px, 100%); max-height: min(80vh, 760px); display: flex; flex-direction: column;
  gap: 8px; padding: 12px; background: #17161d; color: #ececf1; font-size: 13px; line-height: 1.45;
  border: 1px solid #3a3550; border-top: 3px solid var(--kb); border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
}
.popup-head { display: flex; align-items: baseline; gap: 8px; }
.popup-title { font-size: 15px; font-weight: 650; color: var(--k); }
.popup-head .n { font: 600 11px ui-monospace, Menlo, monospace; color: #8d8a9d; }
.popup input.turn-search { margin: 0; }
.popup-list { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; }
.popup-row {
  display: flex; flex-direction: column; gap: 2px; text-align: left; width: 100%;
  background: #1f1d28; border: 1px solid #2c2a36; border-radius: 7px; padding: 6px 9px;
  box-shadow: inset 3px 0 0 var(--kb); color: inherit; font: inherit; cursor: pointer;
}
.popup-row:hover { border-color: var(--kb); }
.popup-line { display: flex; gap: 6px; align-items: center; min-width: 0; }
/* A long title must shrink, never the QA number: squeezed, it wrapped to
   "QA-" over "72". */
.popup-line .tag { flex: none; white-space: nowrap; }
.popup-quote { color: #a9a5ba; font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-left: 2px; }
.popup-foot { display: flex; align-items: center; gap: 6px; border-top: 1px solid #2c2a36; padding-top: 8px; }
.turn-who { display: flex; gap: 7px; align-items: center; font-size: 11px; color: #a9a5ba; }
.turn-who b { color: #ececf1; font-weight: 600; }
.turn-who .av {
  width: 18px; height: 18px; border-radius: 50%; flex: none; text-align: center;
  background: var(--kb); color: #fff; font: 600 9px/18px ui-sans-serif, system-ui, sans-serif;
}
.turn-quote {
  color: #ececf1; padding-left: 10px; border-left: 2px solid var(--kb); white-space: pre-wrap;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden;
}
.turn-acts { display: flex; gap: 6px; align-items: center; }
.turn-rest {
  display: flex; justify-content: space-between; align-items: center; width: 100%;
  margin-top: 12px; padding: 8px 10px; border: 1px dashed #3a3648; border-radius: 8px;
  background: none; color: #a9a5ba; cursor: pointer; font: inherit; font-size: 12px;
}
.turn-rest:hover { color: #ececf1; border-color: #55506b; }

/* "Your turn" on the card: the answer block, in the conversation's blue. */
.answer.ask { border-left-color: #2f4b7a; }
.answer.ask > label { color: #8fb8ff; }

/* "Fixed in a build this environment is not running." Not an error — the deck
   cannot know whether the fix is here, only that the stamps differ — so it
   reads as a warning the tester weighs, in the same amber as a fix claim. */
.note.build-warn { color: #ffc46b; }
.finding .fix-note { margin: 4px 0; padding: 4px 8px; border-left: 2px solid #7a5a24; color: #d8d3e8; white-space: pre-wrap; }
.ref { font: 10px ui-monospace, Menlo, monospace; color: #b9b4d0; word-break: break-all; }

/* The elements picked for a finding, numbered like the outlines on the page
   so "2" in the panel is the "2" the tester sees on the site. */
.picked-list { display: flex; flex-direction: column; gap: 3px; margin-top: 6px; }
.picked { display: flex; align-items: center; gap: 6px; min-width: 0; }
.picked-n { flex: none; min-width: 16px; height: 16px; padding: 0 4px; box-sizing: border-box; border-radius: 8px; background: #7c5cff; color: #fff; font: bold 10px/16px ui-monospace, Menlo, monospace; text-align: center; }
.picked-ref { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; word-break: normal; }
button.picked-x { flex: none; background: none; border: 0; color: #8f89a8; cursor: pointer; font-size: 11px; padding: 0 4px; }
button.picked-x:hover { color: #fff; }

/* Attached screenshots. Fixed-size tiles rather than scaled-to-fit: the panel
   is 380px wide, so a "readable" inline image is a fiction — the tile is a
   handle for opening the real thing in a tab. */
.thumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px; }
.thumb { position: relative; width: 64px; height: 64px; border: 1px solid #413b52; border-radius: 6px; overflow: hidden; background: #2a2638; }
.thumb img { display: block; width: 100%; height: 100%; object-fit: cover; cursor: zoom-in; }
.thumb button.thumb-x {
  position: absolute; top: 0; right: 0; padding: 1px 4px; border: none; cursor: pointer;
  background: rgba(0,0,0,.62); color: #fff; font-size: 10px; line-height: 1.4; border-radius: 0 0 0 6px;
}
.thumb button.thumb-x:hover { background: rgba(214,69,91,.9); }

.note { color: #8d8a9d; font-size: 11px; }
.error { color: #ff8095; font-size: 12px; margin: 6px 0; }
.ok { color: #6fd39a; }
.picking { color: #c7b6ff; }
`;
